import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const benefits = [
  'Exclusive market insights',
  'Business networking opportunities',
  'Priority access to events & delegations',
  'Trade & investment support',
  'Visibility within the business community',
  'Business matchmaking opportunities',
];

// --- Shared Reveal Logic ---
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>('[data-r]').forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = '1';
              node.style.transform = 'translateY(0) translateX(0)';
            }, i * 120);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (dir: 'up' | 'left' | 'right' = 'up'): React.CSSProperties => ({
  opacity: 0,
  transform:
    dir === 'up' ? 'translateY(28px)'
    : dir === 'left' ? 'translateX(-32px)'
    : 'translateX(32px)',
  transition: 'opacity 0.8s ease, transform 0.8s ease',
});
// ---------------------------

export default function MembershipSection() {
  const sectionRef = useReveal();
  const navigate = useNavigate();

  return (
    <section id="membership" className="relative py-24 md:py-32 overflow-hidden font-sans">
      
      {/* Injecting CSS Keyframes for the Infinite Scroll */}
      <style>
        {`
          @keyframes scroll-bg {
            0% { transform: translateX(0); }
            /* Shifts the container exactly 2 image widths to the left to loop perfectly */
            100% { transform: translateX(-66.6666%); } 
          }
          .animate-infinite-bg {
            animation: scroll-bg 90s linear infinite;
          }
        `}
      </style>

      {/* Infinite Scrolling Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Container is 300% width to hold 3 identical-sized images */}
        <div className="absolute top-0 left-0 h-full w-[300%] flex animate-infinite-bg">
          <div className="w-1/3 h-full">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80"
              alt="BICCI membership background 1"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Middle image is flipped horizontally to create a perfect, seamless mirrored seam */}
          <div className="w-1/3 h-full">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80"
              alt="BICCI membership background 2"
              className="w-full h-full object-cover scale-x-[-1]"
            />
          </div>
          <div className="w-1/3 h-full">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80"
              alt="BICCI membership background 3"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Premium Overlay to ensure text readability */}
        <div 
          className="absolute inset-0 backdrop-blur-[2px]" 
          style={{ backgroundColor: 'rgba(15, 28, 53, 0.90)' }} 
        />
      </div>

      {/* Content Container */}
      <div ref={sectionRef} className="relative z-10 w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto flex flex-col items-center text-center">
        
        {/* Heading */}
        <h2 
          data-r 
          style={{
            ...hide('up'),
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            fontWeight: 800,
            letterSpacing: '-0.02em'
          }}
          className="text-white mb-6"
        >
          Join BICCI Today
        </h2>
        
        {/* Description */}
        <p 
          data-r 
          style={hide('up')} 
          className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed"
        >
          Become part of a business network connecting Bangladesh and Italy through trade, investment, innovation, and institutional collaboration.
        </p>

        {/* Benefits List */}
        <div 
          data-r 
          style={hide('up')} 
          className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-14 max-w-4xl mx-auto"
        >
          {benefits.map((benefit) => (
            <div key={benefit} className="flex items-center gap-2.5 text-sm md:text-base text-white/95 font-medium">
              <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#0077b6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* Premium CTA Button */}
        <button
          data-r
          onClick={() => navigate('/membership')}
          className="group inline-flex items-center gap-3 px-8 py-4 text-white font-bold text-sm tracking-wide uppercase rounded-full transition-all duration-300 cursor-pointer shadow-lg hover:-translate-y-1"
          style={{ 
            ...hide('up'), 
            backgroundColor: '#0077b6',
            // boxShadow: '0 4px 14px 0 rgb(0, 119, 182)'
          }}
          // onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 20px rgb(0, 119, 182)'; }}
          // onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px 0 rgb(0, 119, 182)'; }}
        >
          <span>Know More</span>
          <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>

      </div>
    </section>
  );
}